import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Plus, Trash2, Edit2, Loader2, Save, X, 
    Gamepad2, ExternalLink, ChevronLeft, ChevronRight,
    LayoutGrid, Star, Link as LinkIcon, Image as ImageIcon
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { gameService } from '../../../services/gameService';
import { StoreGame } from '../../../types';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { at } from '../../../i18nAdmin';

export const GameStoreTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [games, setGames] = useState<StoreGame[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isFormAnimating, setIsFormAnimating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Delete Modal State
    const [deleteModalState, setDeleteModalState] = useState<{ isOpen: boolean; id: string | null; isDeleting: boolean }>({
        isOpen: false,
        id: null,
        isDeleting: false
    });

    // Form State
    const [formData, setFormData] = useState<Omit<StoreGame, 'id'>>({
        title: '',
        category: '',
        icon: '',
        isNew: false,
        link: '',
        is_hidden: false
    });

    const loadData = async () => {
        try {
            setIsLoading(true);
            const data = await gameService.getStoreGames();
            setGames(data);
        } catch (e) {
            showToast('error', 'Failed to load games');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtering Logic
    const filteredGames = useMemo(() => {
        return games.filter(g => 
            g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (g.category || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [games, searchTerm]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredGames.length / pageSize);
    const paginatedGames = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredGames.slice(start, start + pageSize);
    }, [filteredGames, currentPage, pageSize]);

    const handleOpenCreate = () => {
        setEditingId(null);
        setFormData({
            title: '',
            category: '',
            icon: '',
            isNew: false,
            link: '',
            is_hidden: false
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const handleOpenEdit = (game: StoreGame) => {
        setEditingId(game.id);
        setFormData({
            title: game.title,
            category: game.category || '',
            icon: game.icon || '',
            isNew: !!game.isNew,
            link: game.link || '',
            is_hidden: !!game.is_hidden
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const closeForm = () => {
        setIsFormAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            setEditingId(null);
        }, 500);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (editingId) {
                await gameService.updateStoreGame(editingId, formData);
                showToast('success', at(lang, 'configSaved'));
            } else {
                await gameService.createStoreGame(formData);
                showToast('success', at(lang, 'gameCreated'));
            }
            closeForm();
            loadData();
        } catch (e: any) {
            showToast('error', e.message || 'Operation failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (id: string) => {
        setDeleteModalState({ isOpen: true, id, isDeleting: false });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModalState.id) return;
        try {
            setDeleteModalState(prev => ({ ...prev, isDeleting: true }));
            await gameService.deleteStoreGame(deleteModalState.id);
            showToast('success', at(lang, 'deletedSuccess'));
            loadData();
        } catch (e) {
            showToast('error', 'Delete failed');
        } finally {
            setDeleteModalState({ isOpen: false, id: null, isDeleting: false });
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
                        {at(lang, 'manageGames')}
                    </h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        {at(lang, 'manageGamesDesc')}
                    </p>
                </div>
                <button 
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
                >
                    <Plus size={16} strokeWidth={3} />
                    {at(lang, 'addNew')}
                </button>
            </div>

            {/* Search Bar */}
            <div className="relative group max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input 
                    type="text" 
                    placeholder={at(lang, 'search')} 
                    className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[250px]">{at(lang, 'gameTitle')}</th>
                                <th className="px-6 py-5 min-w-[120px]">{at(lang, 'category')}</th>
                                <th className="px-6 py-5 min-w-[100px]">{at(lang, 'status')}</th>
                                <th className="px-8 py-5 text-right min-w-[120px]">{at(lang, 'ops')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center">
                                        <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={32} />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Loading Games...</p>
                                    </td>
                                </tr>
                            ) : paginatedGames.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center opacity-30">
                                        <Gamepad2 size={48} className="mx-auto mb-4" />
                                        <p className="text-sm font-black uppercase tracking-widest">No Games Found</p>
                                    </td>
                                </tr>
                            ) : (
                                paginatedGames.map((game) => (
                                    <tr key={game.id} className={`md:hover:bg-slate-50/50 transition-colors group ${game.is_hidden ? 'opacity-50 grayscale' : ''}`}>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center text-2xl shadow-sm">
                                            {game.icon ? (
                                                game.icon
                                            ) : (
                                                <Gamepad2 size={20} className="text-slate-300" />
                                            )}
                                        </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-black text-slate-900 truncate uppercase tracking-tight">
                                                        {game.title}
                                                    </p>
                                                    <p className="text-[10px] font-bold text-slate-400 truncate tracking-tight">
                                                        {game.link || '-'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <span className="text-[10px] font-black px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg uppercase tracking-widest">
                                                {game.category || 'General'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex flex-col gap-1.5">
                                                {game.isNew && (
                                                    <span className="w-fit inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm">
                                                        <Star size={8} fill="currentColor" /> NEW
                                                    </span>
                                                )}
                                                {game.is_hidden && (
                                                    <span className="w-fit inline-flex items-center gap-1 px-2 py-0.5 bg-slate-900 text-white rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm">
                                                        HIDDEN
                                                    </span>
                                                )}
                                                {!game.isNew && !game.is_hidden && (
                                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenEdit(game)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                    title={at(lang, 'editGame')}
                                                >
                                                    <Edit2 size={16}/>
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteClick(game.id)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Page {currentPage} of {totalPages || 1}
                    </p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={totalPages === 0 || currentPage === totalPages}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {isFormOpen && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !m-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeForm}></div>
                    <div className={`relative bg-white w-full max-w-lg rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                {editingId ? at(lang, 'editGame') : at(lang, 'newGame')}
                            </h3>
                            <button onClick={closeForm} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>

                        <form onSubmit={handleSave} className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                    <Gamepad2 size={12} /> {at(lang, 'gameTitle')}
                                </label>
                                <input 
                                    required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={formData.title}
                                    onChange={e => setFormData({...formData, title: e.target.value})}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                        <LayoutGrid size={12} /> {at(lang, 'category')}
                                    </label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.category || ''}
                                        onChange={e => setFormData({...formData, category: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                        <Star size={12} /> {at(lang, 'isNew')}
                                    </label>
                                    <div className="flex items-center h-[46px] px-2">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="sr-only peer"
                                                checked={formData.isNew}
                                                onChange={e => setFormData({...formData, isNew: e.target.checked})}
                                            />
                                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-500"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                    <ImageIcon size={12} /> {at(lang, 'iconEmoji')}
                                </label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={formData.icon || ''}
                                    onChange={e => setFormData({...formData, icon: e.target.value})}
                                    placeholder="e.g. ⚽️"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                    <LinkIcon size={12} /> {at(lang, 'gameLink')}
                                </label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={formData.link || ''}
                                    onChange={e => setFormData({...formData, link: e.target.value})}
                                    placeholder="/game-store/..."
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                    HIDDEN
                                </label>
                                <div className="flex items-center h-[46px] px-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer"
                                            checked={formData.is_hidden}
                                            onChange={e => setFormData({...formData, is_hidden: e.target.checked})}
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                                    </label>
                                    <span className="ml-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        {formData.is_hidden ? 'HIDDEN FROM USERS' : 'VISIBLE TO USERS'}
                                    </span>
                                </div>
                            </div>

                            <button 
                                type="submit"
                                disabled={isSaving}
                                className="w-full py-4 bg-slate-900 text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-200 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {at(lang, 'saveGame')}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, id: null, isDeleting: false })}
                onConfirm={handleConfirmDelete}
                isLoading={deleteModalState.isDeleting}
                title={at(lang, 'deleteConfirm')}
                description={at(lang, 'deleteConfirmDesc')}
                confirmLabel={at(lang, 'delete')}
                cancelLabel={at(lang, 'cancel')}
            />
        </div>
    );
};
